import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listOrgUnits, createOrgUnit, createGrant, listAllGrants, setTenantAdmin, resolveIdpUser, type OrgUnit } from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import { IdpUserPicker } from "./IdpUserPicker";
import { tenantAdminSubject, idpUserLabel } from "./tenantAdmins";
import css from "./WarehouseAdminPage.module.css";

/**
 * Tenants (org-units of type `tenant`) are the top of the hierarchy — every site belongs to
 * one (ADR 167). A super-admin on an empty system starts here: create the first tenant (and,
 * optionally, its admin) before any site can exist.
 */
export function TenantsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [adminSubject, setAdminSubject] = useState("");

  const { data: tenants = [] } = useQuery({ queryKey: ["org-units"], queryFn: listOrgUnits });
  const { data: grants = [] } = useQuery({ queryKey: ["grants"], queryFn: listAllGrants });

  const create = useMutation({
    mutationFn: async () => {
      const ou = await createOrgUnit({ type: "tenant", name: name.trim() });
      if (adminSubject.trim()) {
        await createGrant({ subject: adminSubject.trim(), scopeType: "org_unit", scopeId: ou.id, role: "admin" });
      }
      return ou;
    },
    onSuccess: () => {
      setName(""); setAdminSubject("");
      qc.invalidateQueries({ queryKey: ["org-units"] });
      qc.invalidateQueries({ queryKey: ["me-org-units"] });
      qc.invalidateQueries({ queryKey: ["grants"] });
      // Refresh the store so the nav discloses Sites now that a tenant exists (ADR 178).
      void useSiteStore.getState().loadAdminOrgUnits();
    },
  });

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate();
  }

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.title}>{t("nav.tenants")}</div>
      </div>
      <p className={css.subtitle}>{t("tenantsAdmin.subtitle")}</p>

      <form className={css.form} onSubmit={onCreate}>
        <div className={css.field}>
          <label className={css.label}>{t("common.col.name")}</label>
          <input className={css.input} value={name}
                 onChange={(e) => setName(e.target.value)}
                 placeholder={t("tenantsAdmin.namePlaceholder")} />
        </div>
        <div className={css.field}>
          <label className={css.label}>{t("tenantsAdmin.adminSubjectLabel")}</label>
          <IdpUserPicker value={adminSubject} onChange={setAdminSubject}
                         inputClassName={css.input}
                         placeholder={t("tenantsAdmin.adminSubjectPlaceholder")} />
          <p className={css.help}>{t("tenantsAdmin.adminSubjectHelp")}</p>
        </div>
        <button type="submit" className={css.btn} disabled={create.isPending || !name.trim()}>
          {create.isPending ? t("common.creating") : t("common.create")}
        </button>
      </form>

      <table className={css.table}>
        <thead>
          <tr>
            <th>{t("common.col.id")}</th><th>{t("common.col.name")}</th>
            <th>{t("tenantsAdmin.col.admin")}</th><th>{t("common.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((ou) => (
            <TenantRow key={ou.id} tenant={ou} adminSubject={tenantAdminSubject(grants, ou.id)}
                       onChanged={() => qc.invalidateQueries({ queryKey: ["grants"] })} />
          ))}
        </tbody>
      </table>
      {tenants.length === 0 && <p className={css.empty}>{t("tenantsAdmin.empty")}</p>}
    </div>
  );
}

/**
 * A tenant row with inline admin management (ADR 180). Global-admin only — the whole page is
 * (nav minRole global-admin + assertGlobalAdmin server-side). Setting/clearing the admin only
 * touches this tenant's org_unit/admin grant; it can never affect the global-admin superuser.
 */
function TenantRow({ tenant, adminSubject, onChanged }: {
  tenant: OrgUnit;
  adminSubject: string | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState("");

  const save = useMutation({
    mutationFn: (subj: string) => setTenantAdmin(tenant.id, subj.trim()),
    onSuccess: () => { setEditing(false); setSubject(""); onChanged(); },
  });

  // Resolve the admin's sub → email for display (ADR 181); falls back to the sub.
  const { data: adminUser } = useQuery({
    queryKey: ["idpUser", adminSubject],
    queryFn: () => resolveIdpUser(adminSubject as string),
    enabled: !!adminSubject,
    staleTime: 300_000,
  });

  return (
    <tr>
      <td>{tenant.id}</td>
      <td>{tenant.name}</td>
      <td className={css.nodeSub} title={adminSubject ?? undefined}>{idpUserLabel(adminUser, adminSubject)}</td>
      <td className={css.actions}>
        {editing ? (
          <div className={css.inlineRow}>
            <IdpUserPicker value={subject} onChange={setSubject}
                           inputClassName={css.input}
                           placeholder={t("tenantsAdmin.adminSubjectPlaceholder")} />
            <button className={css.btn} disabled={save.isPending || !subject.trim()}
                    onClick={() => save.mutate(subject)}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </button>
            {adminSubject && (
              <button className={css.linkBtn} disabled={save.isPending}
                      onClick={() => save.mutate("")}>
                {t("tenantsAdmin.clearAdmin")}
              </button>
            )}
            <button className={css.linkBtn} onClick={() => { setEditing(false); setSubject(""); }}>
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <button className={css.linkBtn} onClick={() => setEditing(true)}>
            {adminSubject ? t("tenantsAdmin.changeAdmin") : t("tenantsAdmin.setAdmin")}
          </button>
        )}
      </td>
    </tr>
  );
}
